import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, request } from "../../lib/api";
import { formatBytes, formatDate, formatDateTime, titleCase } from "../../lib/format";
import type { AdminTenant, KYCApplication } from "../../lib/types";
import {
  Button,
  ErrorNotice,
  Modal,
  Mono,
  PageHeader,
  TextAreaField,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
} from "../../components/ui";

interface ReviewResponse {
  application: KYCApplication;
  tenant: AdminTenant;
  /** The reviewer sees the unmasked account, because checking it against the bank
   *  letter is the point of the review. */
  settlement_account_full: {
    account_name: string;
    account_number: string;
    bank_name: string;
    branch_code: string;
  };
}

type Decision = "approve" | "reject" | "request-info" | null;

export function KycReview() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<Decision>(null);
  const [note, setNote] = useState("");

  const review = useQuery({
    queryKey: ["kyc-review", id],
    queryFn: () => api.get<ReviewResponse>(`/api/admin/kyc/${id}`),
  });

  const startReview = useMutation({
    mutationFn: () => api.post(`/api/admin/kyc/${id}/start-review`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["kyc-review", id] }),
  });

  const decide = useMutation({
    mutationFn: () => {
      if (decision === "approve") return api.post(`/api/admin/kyc/${id}/approve`, { note });
      if (decision === "reject") return api.post(`/api/admin/kyc/${id}/reject`, { reason: note });
      return api.post(`/api/admin/kyc/${id}/request-info`, { message: note });
    },
    onSuccess: () => {
      setDecision(null);
      setNote("");
      queryClient.invalidateQueries({ queryKey: ["kyc-review", id] });
      queryClient.invalidateQueries({ queryKey: ["kyc-queue"] });
    },
  });

  if (review.isLoading) {
    return (
      <Sheet>
        <Skeleton rows={10} />
      </Sheet>
    );
  }
  if (review.error) return <ErrorNotice error={review.error} />;

  const { application: app, tenant, settlement_account_full: account } = review.data!;
  const decided = app.status === "approved" || app.status === "rejected";

  return (
    <>
      <Link
        to="/admin/verification"
        className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] text-ink-muted transition-colors hover:text-ink"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Verification queue
      </Link>

      <PageHeader
        eyebrow="Verification review"
        title={tenant.legal_name}
        description={`Registered ${formatDate(tenant.created_at)} · ${tenant.contact_email}`}
        actions={<StatusPill status={app.status} />}
      />

      {app.status === "submitted" && (
        <div className="settle mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-indigo-ink/25 bg-indigo-wash px-5 py-4">
          <p className="text-[13px] text-ink">
            Claim this application before reviewing it, so no one else works on it at the same time.
          </p>
          <Button variant="primary" loading={startReview.isPending} onClick={() => startReview.mutate()}>
            Start review
          </Button>
        </div>
      )}

      {decided && app.decision_reason && (
        <div
          className={`settle mb-6 rounded-md border px-4 py-3 ${
            app.status === "approved"
              ? "border-verdigris/25 bg-verdigris-wash"
              : "border-sienna/25 bg-sienna-wash"
          }`}
        >
          <p className="label mb-1">Decision recorded {app.reviewed_at && formatDateTime(app.reviewed_at)}</p>
          <p className="text-[13px] text-ink">{app.decision_reason}</p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <div className="space-y-6">
          <Sheet className="settle settle-1 overflow-hidden">
            <SheetHeader title="Business" />
            <dl className="divide-y divide-rule text-[13px]">
              <Row label="Legal name" value={tenant.legal_name} />
              <Row label="Trading name" value={tenant.trading_name || "—"} />
              <Row label="Business type" value={titleCase(app.business_type) || "—"} />
              <Row label="Industry" value={titleCase(app.industry_code) || "—"} />
              <Row
                label="Registered address"
                value={
                  [
                    app.registered_address?.Line1,
                    app.registered_address?.City,
                    app.registered_address?.Province,
                    app.registered_address?.Country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"
                }
              />
              <Row
                label="Expected monthly volume"
                value={`${(app.estimated_monthly_volume_minor / 100).toLocaleString("en-ZW", {
                  minimumFractionDigits: 2,
                })} ${app.estimated_volume_currency}`}
              />
            </dl>
          </Sheet>

          <Sheet className="settle settle-2 overflow-hidden">
            <SheetHeader
              title="Settlement account"
              description="Check this against the bank confirmation letter"
            />
            <dl className="divide-y divide-rule text-[13px]">
              <Row label="Account name" value={account.account_name || "—"} />
              <Row
                label="Account number"
                value={account.account_number ? <Mono copyable>{account.account_number}</Mono> : "—"}
              />
              <Row label="Bank" value={account.bank_name || "—"} />
              <Row label="Branch code" value={account.branch_code || "—"} />
            </dl>
            {account.account_name &&
              tenant.legal_name &&
              !namesLikelyMatch(account.account_name, tenant.legal_name) && (
                <div className="border-t border-rule bg-brass-wash px-5 py-3">
                  <p className="text-[12.5px] text-brass">
                    The account name does not obviously match the registered company name. Confirm
                    before approving.
                  </p>
                </div>
              )}
          </Sheet>

          <Sheet className="settle settle-3 overflow-hidden">
            <SheetHeader title="Directors and beneficial owners" />
            {app.directors.length === 0 ? (
              <p className="px-5 py-6 text-[13px] text-sienna">
                None declared — this application cannot be approved.
              </p>
            ) : (
              <ul className="divide-y divide-rule">
                {app.directors.map((director) => (
                  <li key={director.id} className="flex items-center justify-between gap-4 px-5 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-[13.5px] text-ink">{director.full_name}</p>
                      <p className="mt-0.5 font-mono text-[11.5px] text-ink-faint">
                        {director.national_id || "No identity number recorded"}
                        {director.nationality && ` · ${director.nationality}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {director.is_pep && (
                        <span className="rounded-full bg-brass-wash px-2 py-0.5 text-[10.5px] font-medium text-brass">
                          PEP — enhanced diligence
                        </span>
                      )}
                      <span className="font-mono text-[12px] tabular text-ink-muted">
                        {director.ownership_percent}%
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Sheet>

          <Sheet className="settle settle-4 overflow-hidden">
            <SheetHeader title="Documents" description="Open each one and check it against the details above" />
            {app.documents.length === 0 ? (
              <p className="px-5 py-6 text-[13px] text-sienna">No documents uploaded.</p>
            ) : (
              <ul className="divide-y divide-rule">
                {app.documents.map((doc) => (
                  <li key={doc.id} className="flex items-center justify-between gap-4 px-5 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-[13px] text-ink">{titleCase(doc.type)}</p>
                      <p className="mt-0.5 truncate text-[11.5px] text-ink-faint">
                        {doc.filename} · {formatBytes(doc.size_bytes)} · {formatDateTime(doc.uploaded_at)}
                      </p>
                    </div>
                    <DocumentLink applicationId={id} documentId={doc.id} filename={doc.filename} />
                  </li>
                ))}
              </ul>
            )}
          </Sheet>
        </div>

        <div className="space-y-6">
          <Sheet className="settle settle-2 overflow-hidden">
            <SheetHeader title="Decision" />
            <div className="space-y-2.5 px-5 py-4">
              {decided ? (
                <p className="text-[13px] text-ink-muted">
                  This application has already been {app.status}.
                </p>
              ) : (
                <>
                  <Button
                    variant="primary"
                    className="w-full"
                    disabled={app.status !== "in_review"}
                    onClick={() => setDecision("approve")}
                  >
                    Approve for live payments
                  </Button>
                  <Button
                    className="w-full"
                    disabled={app.status === "draft"}
                    onClick={() => setDecision("request-info")}
                  >
                    Request more information
                  </Button>
                  <Button
                    variant="danger"
                    className="w-full"
                    disabled={app.status === "draft"}
                    onClick={() => setDecision("reject")}
                  >
                    Reject
                  </Button>
                  {app.status === "submitted" && (
                    <p className="pt-1 text-[11.5px] leading-relaxed text-ink-muted">
                      Start the review before approving. Rejecting or requesting information can be
                      done straight away.
                    </p>
                  )}
                </>
              )}
            </div>
          </Sheet>

          <Sheet className="settle settle-3 overflow-hidden">
            <SheetHeader title="Reviewer checklist" />
            <ul className="space-y-2.5 px-5 py-4 text-[12.5px] leading-relaxed text-ink-muted">
              {[
                "Certificate of incorporation matches the registered name and number.",
                "CR14 lists the directors declared above.",
                "Tax clearance is current.",
                "Bank letter shows the same account name as the company.",
                "Each director has an identity document on file.",
                "Nothing suggests the business is outside our accepted activities.",
              ].map((item) => (
                <li key={item} className="flex gap-2.5">
                  <span className="mt-[8px] h-px w-3 shrink-0 bg-rule-strong" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </Sheet>
        </div>
      </div>

      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={
          decision === "approve"
            ? "Approve this firm?"
            : decision === "reject"
              ? "Reject this application?"
              : "Request more information"
        }
        description={
          decision === "approve"
            ? "They will be able to issue live keys and take real money immediately."
            : decision === "reject"
              ? "Their live keys will be revoked and they will see your reason."
              : "The application returns to them for correction, with your message."
        }
        footer={
          <>
            <Button onClick={() => setDecision(null)}>Cancel</Button>
            <Button
              variant={decision === "reject" ? "danger" : "primary"}
              loading={decide.isPending}
              disabled={decision !== "approve" && note.trim() === ""}
              onClick={() => decide.mutate()}
            >
              {decision === "approve" ? "Approve" : decision === "reject" ? "Reject" : "Send request"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <ErrorNotice error={decide.error} />
          <TextAreaField
            label={decision === "approve" ? "Note (optional)" : "Reason — the merchant will read this"}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            required={decision !== "approve"}
            placeholder={
              decision === "approve"
                ? "Documents verified against CR14 and the bank letter."
                : "Be specific about what needs to change so they can correct it."
            }
          />
        </div>
      </Modal>
    </>
  );
}

/** DocumentLink downloads a document through the authenticated API rather than a bare
 *  href, because the endpoint requires a bearer token that a plain link cannot carry. */
function DocumentLink({
  applicationId,
  documentId,
  filename,
}: {
  applicationId: string;
  documentId: string;
  filename: string;
}) {
  const [busy, setBusy] = useState(false);

  async function open() {
    setBusy(true);
    try {
      const response = await request<Response>(`/api/admin/kyc/${applicationId}/documents/${documentId}`, {
        raw: true,
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      // Revoked on the next tick so the download has started before the URL is freed.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button size="sm" loading={busy} onClick={open}>
      Open
    </Button>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-2.5">
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      <dd className="min-w-0 text-right text-ink">{value}</dd>
    </div>
  );
}

/** namesLikelyMatch is a deliberately loose check used only to raise a prompt for the
 *  reviewer. It never blocks anything: the decision stays with the human. */
function namesLikelyMatch(a: string, b: string): boolean {
  const normalise = (s: string) =>
    s
      .toLowerCase()
      .replace(/\b(private|pvt|limited|ltd|company|co|inc|\(|\))\b/g, "")
      .replace(/[^a-z0-9]/g, "");
  const left = normalise(a);
  const right = normalise(b);
  return left.includes(right) || right.includes(left);
}
